export interface PageConfig {
  pageKey: string;
  displayName: string;
  tourId: string | null;
  wikiUrl: string | null;
}

// All wiki links point to smarfac.com as placeholder (to be updated with real URLs later)
const WIKI_PLACEHOLDER = 'https://smarfac.com';

export const PAGE_CONFIGS: Record<string, PageConfig> = {
  batch: {
    pageKey: 'batch',
    displayName: 'Batch',
    tourId: 'batch-general',
    wikiUrl: WIKI_PLACEHOLDER,
  },
  timeline: {
    pageKey: 'timeline',
    displayName: 'Timeline',
    tourId: null,
    wikiUrl: WIKI_PLACEHOLDER,
  },
  planboard: {
    pageKey: 'planboard',
    displayName: 'Planboard',
    tourId: 'planboard-overview',
    wikiUrl: WIKI_PLACEHOLDER,
  },
  machines: {
    pageKey: 'machines',
    displayName: 'Machines',
    tourId: null,
    wikiUrl: WIKI_PLACEHOLDER,
  },
  job: {
    pageKey: 'job',
    displayName: 'Job',
    tourId: null,
    wikiUrl: WIKI_PLACEHOLDER,
  },
  part: {
    pageKey: 'part',
    displayName: 'Part',
    tourId: null,
    wikiUrl: WIKI_PLACEHOLDER,
  },
  ncfile: {
    pageKey: 'ncfile',
    displayName: 'NC File',
    tourId: null,
    wikiUrl: WIKI_PLACEHOLDER,
  },
  search: {
    pageKey: 'search',
    displayName: 'Search',
    tourId: null,
    wikiUrl: WIKI_PLACEHOLDER,
  },
  wallboard: {
    pageKey: 'wallboard',
    displayName: 'Wallboard',
    tourId: 'wallboard-overview',
    wikiUrl: WIKI_PLACEHOLDER,
  },
  settings: {
    pageKey: 'settings',
    displayName: 'Settings',
    tourId: null,
    wikiUrl: WIKI_PLACEHOLDER,
  },
};

/**
 * Detects the current page based on the route name.
 * Route patterns:
 * - Machines (not details) -> machines
 * - *NowBatch* or BatchDetails* -> batch
 * - *Timeline* -> timeline
 * - Planboard* -> planboard
 * - *NowJob* or JobDetails* -> job
 * - *NowPart* or PartDetails* -> part
 * - *NowNCFile* or NCFileDetails* -> ncfile
 * - Search* -> search
 * - Wallboard* -> wallboard
 * - Settings* -> settings
 */
export function detectCurrentPage(routeName: string | undefined): PageConfig | null {
  if (!routeName) {
    return null;
  }

  const name = routeName.toLowerCase();

  // Check batch patterns first (NowBatch, BatchDetails)
  if (name.includes('nowbatch') || name.includes('batchdetails')) {
    return PAGE_CONFIGS.batch;
  }

  // Check timeline
  if (name.includes('timeline')) {
    return PAGE_CONFIGS.timeline;
  }

  // Check planboard
  if (name.includes('planboard')) {
    return PAGE_CONFIGS.planboard;
  }

  // Check job patterns (NowJob, JobDetails)
  if (name.includes('nowjob') || name.includes('jobdetails')) {
    return PAGE_CONFIGS.job;
  }

  // Check part patterns (NowPart, PartDetails)
  if (name.includes('nowpart') || name.includes('partdetails')) {
    return PAGE_CONFIGS.part;
  }

  // Check NC file patterns (NowNCFile, NCFileDetails)
  if (name.includes('nowncfile') || name.includes('ncfiledetails')) {
    return PAGE_CONFIGS.ncfile;
  }

  // Check search
  if (name.includes('search')) {
    return PAGE_CONFIGS.search;
  }

  // Check wallboard
  if (name.includes('wallboard')) {
    return PAGE_CONFIGS.wallboard;
  }

  // Check settings
  if (name.includes('settings')) {
    return PAGE_CONFIGS.settings;
  }

  // Check machines (Authorized route = Machines main page)
  // Must be checked last since it's the most generic
  if (name === 'authorized' || (name.includes('machines') && !name.includes('details'))) {
    return PAGE_CONFIGS.machines;
  }

  return null;
}

/**
 * Detects the current page based on the URL pathname.
 * URL patterns:
 * - /machines, / -> machines (main page)
 * - /machines/now/batch/* or /machines/batch/* -> batch
 * - /machines/now/timeline/* or /machines/timeline/* -> timeline
 * - /planboard* -> planboard
 * - /machines/now/job/* or /machines/job/* -> job
 * - /machines/now/part/* or /machines/part/* -> part
 * - /machines/now/ncfile/* -> ncfile
 * - /search* -> search
 * - /wallboard* -> wallboard
 * - /settings* -> settings
 */
export function detectCurrentPageFromPath(pathname: string | undefined): PageConfig | null {
  if (!pathname) {
    return null;
  }

  const path = pathname.toLowerCase();

  // Check batch patterns
  if (path.includes('/batch')) {
    return PAGE_CONFIGS.batch;
  }

  // Check timeline
  if (path.includes('/timeline')) {
    return PAGE_CONFIGS.timeline;
  }

  // Check planboard
  if (path.includes('/planboard')) {
    return PAGE_CONFIGS.planboard;
  }

  // Check job patterns
  if (path.includes('/job')) {
    return PAGE_CONFIGS.job;
  }

  // Check part patterns
  if (path.includes('/part')) {
    return PAGE_CONFIGS.part;
  }

  // Check NC file patterns
  if (path.includes('/ncfile')) {
    return PAGE_CONFIGS.ncfile;
  }

  // Check search
  if (path.includes('/search')) {
    return PAGE_CONFIGS.search;
  }

  // Check wallboard
  if (path.includes('/wallboard')) {
    return PAGE_CONFIGS.wallboard;
  }

  // Check settings
  if (path.includes('/settings')) {
    return PAGE_CONFIGS.settings;
  }

  // Check machines (main page, or /machines path)
  if (path === '/' || path.includes('/machines')) {
    return PAGE_CONFIGS.machines;
  }

  return null;
}
